import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react';
import { accessLink, currentAudience, readAccess } from '../accessState';
import { listAccesses, selectAccess } from '../accessSession';
import type { AccessChoice } from '@biasafe.models';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** Always name the current firm and audience; never infer them from an email. */
export function AccessSwitcher() {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const audience = currentAudience();
  const selected = readAccess()?.choice;
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['auth', 'accesses', audience],
    queryFn: listAccesses,
    enabled: open,
    retry: false,
    staleTime: 0,
  });
  const choose = async (choice: AccessChoice) => {
    if (choice.id === selected?.id) {
      setOpen(false);
      return;
    }
    if (choice.audience !== audience) {
      window.location.assign(accessLink(choice));
      return;
    }
    setPending(true);
    setError(false);
    try {
      await selectAccess(choice.id);
      queryClient.clear();
      window.location.assign(audience === 'portal' ? '/portal' : '/dashboard');
    } catch {
      setError(true);
      setPending(false);
    }
  };
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="min-w-0 gap-2"
          aria-label={`Switch Workspace, ${selected?.organizationName ?? 'Your Workspace'}, ${audience === 'advisor' ? 'Advisor' : 'Client'}`}
          title={selected?.organizationName}
          disabled={pending}
        >
          <Building2 className="hidden size-4 shrink-0 text-muted-foreground sm:block" />
          <span className="hidden max-w-36 truncate text-xs sm:inline">
            {selected?.organizationName ?? 'Your Workspace'}
          </span>
          <span className="rounded border border-primary/20 bg-primary/5 px-1.5 py-0.5 text-[11px] font-medium text-primary">
            {audience === 'advisor' ? 'Advisor' : 'Client'}
          </span>
          <ChevronDown className="hidden size-3 shrink-0 text-muted-foreground sm:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-80 max-w-[calc(100vw-2rem)]"
      >
        <DropdownMenuLabel>Switch Workspace</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {query.isPending && (
          <p
            role="status"
            className="flex items-center gap-2 p-3 text-sm text-muted-foreground"
          >
            <Loader2 className="size-4 animate-spin" />
            Loading accesses…
          </p>
        )}
        {(query.isError || error) && (
          <p role="alert" className="p-3 text-sm text-destructive">
            Could not load or switch access.{' '}
            <button
              className="underline"
              onClick={() => {
                setError(false);
                void query.refetch();
              }}
            >
              Try Again
            </button>
          </p>
        )}
        {query.data?.accesses.map((choice) => (
          <DropdownMenuItem
            key={choice.id}
            disabled={pending}
            onSelect={(event) => {
              event.preventDefault();
              void choose(choice);
            }}
            className="gap-3 py-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{choice.organizationName}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {choice.audience === 'advisor'
                  ? 'Advisor Workspace'
                  : `Client Portal · ${choice.displayName}`}
              </p>
            </div>
            {choice.id === selected?.id && (
              <Check
                className="size-4 text-primary"
                aria-label="Current Access"
              />
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <p className="px-2 py-2 text-xs text-muted-foreground">
          Switching portals keeps your other session open. Sign-in may be
          required on the other portal.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
